import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { showJobSuccessModal } from '@client/components/_shared/jobs/JobSuccessModal';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { ImportMasterObligationModal } from '@client/components/obligations/ImportMasterObligationModal';
import { SendObligationToOrganizationModal } from '@client/components/obligations/SendObligationToOrganizationModal';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Dropdown, Modal, Space, Tooltip } from 'antd';
import { Brain, Clock, Download, FileSearch, RefreshCw, Send, Tag, Tags, Upload } from 'lucide-react';
import { useState } from 'react';
import { type CustomLabelConfig, type QueryParams, useMasterObligationColumns } from './columns';
import { MasterObligationFilters } from './MasterObligationFilters';

export const Route = createFileRoute('/admin-console/platform-content/obligations/library/')({
    staticData: {
        ui: {
            title: 'Obligations library',
            subtitle: 'Manage your obligations library.',
            widthVariant: 'fullWidth',
            menuDescription: 'Master obligations library',
        },
    },
    component: MasterObligations,
});

function MasterObligations() {
    const { trpc, trpcClient } = useTrpc();
    const navigate = useNavigate();
    const { isReadOnly } = useAdminModulePermissions('content');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isImportModalVisible, setIsImportModalVisible] = useState(false);
    const [isConfirmForceUpdateVisible, setIsConfirmForceUpdateVisible] = useState(false);
    const [isConfirmForceCategoriseVisible, setIsConfirmForceCategoriseVisible] = useState(false);
    const [isConfirmForceIdentifyFsgVisible, setIsConfirmForceIdentifyFsgVisible] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
        where: {},
    });

    const { data: customLabelsConfig } = useConfigServiceLoader((service) => service.fetchMasterObligationLabels());

    const { data, isPending, refetch } = trpc.admin.masterObligation.listMasterObligations.useQuery(queryParams);
    const { mutate: deleteObligation } = trpc.admin.masterObligation.deleteMasterObligation.useMutation();

    const { mutate: generateEmbeddings, isPending: generateEmbeddingsLoading } =
        trpc.admin.masterObligation.generateEmbeddings.useMutation();

    const { mutate: categorizeObligations, isPending: categorizeLoading } =
        trpc.admin.masterObligation.categorizeObligations.useMutation();

    const { mutate: identifyFsgObligations, isPending: identifyFsgLoading } =
        trpc.admin.masterObligation.identifyFsgObligations.useMutation();

    const onDelete = (id: bigint) => {
        deleteObligation(
            { id },
            {
                onSuccess() {
                    void refetch();
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    };

    const columns = useMasterObligationColumns(
        customLabelsConfig as CustomLabelConfig[] | undefined,
        onDelete,
        isReadOnly,
    );

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
        },
    });

    const handleExport = async () => {
        try {
            setIsExporting(true);
            message.loading('Exporting master obligations...');

            const result = await trpcClient.admin.masterObligation.exportMasterObligations.query({
                where: queryParams.where,
            });

            const blob = new Blob(['\uFEFF' + result.csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            message.destroy();
            message.success(`Exported ${result.count} master obligations`);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleGenerateEmbeddings = (mode: 'stale' | 'all') => {
        if (mode === 'all') {
            setIsConfirmForceUpdateVisible(true);
        } else {
            message.loading('Updating stale embeddings...');
            generateEmbeddings(
                { mode: 'stale' },
                {
                    onSuccess(data) {
                        message.destroy();
                        const jobId = data?.jobId;
                        if (jobId) {
                            showJobSuccessModal({
                                jobId,
                                title: 'Stale embeddings update queued',
                                message: 'The stale embeddings update has been queued for processing.',
                                description:
                                    'The process will run in the background and may take a few minutes depending on the number of stale embeddings.',
                            });
                        }
                    },
                    onError(error) {
                        errorMessage.show(error);
                    },
                },
            );
        }
    };

    const embeddingMenuItems = [
        {
            key: 'stale',
            icon: <Clock className="h-4 w-4" />,
            label: 'Update stale embeddings',
            onClick: () => handleGenerateEmbeddings('stale'),
        },
        {
            key: 'all',
            icon: <RefreshCw className="h-4 w-4" />,
            label: 'Force update all embeddings',
            onClick: () => handleGenerateEmbeddings('all'),
        },
    ];

    const handleCategorize = (mode: 'uncategorised' | 'all') => {
        if (mode === 'all') {
            setIsConfirmForceCategoriseVisible(true);
        } else {
            message.loading('Categorising uncategorised obligations...');
            categorizeObligations(
                { mode: 'uncategorised' },
                {
                    onSuccess(data) {
                        message.destroy();
                        const jobId = data?.jobId;
                        if (jobId) {
                            showJobSuccessModal({
                                jobId,
                                title: 'Categorisation queued',
                                message: 'The categorisation job has been queued for processing.',
                                description:
                                    'The process will run in the background and may take a few minutes depending on the number of uncategorised obligations.',
                            });
                        }
                    },
                    onError(error) {
                        errorMessage.show(error);
                    },
                },
            );
        }
    };

    const handleIdentifyFsg = (mode: 'unidentified' | 'all') => {
        if (mode === 'all') {
            setIsConfirmForceIdentifyFsgVisible(true);
        } else {
            message.loading('Identifying FSG obligations...');
            identifyFsgObligations(
                { mode: 'unidentified' },
                {
                    onSuccess(data) {
                        message.destroy();
                        const jobId = data?.jobId;
                        if (jobId) {
                            showJobSuccessModal({
                                jobId,
                                title: 'FSG identification queued',
                                message: 'The FSG identification job has been queued for processing.',
                                description:
                                    'The process will run in the background and may take a few minutes depending on the number of obligations to check.',
                            });
                        }
                    },
                    onError(error) {
                        errorMessage.show(error);
                    },
                },
            );
        }
    };

    const categoriseMenuItems = [
        {
            key: 'uncategorised',
            icon: <Tag className="h-4 w-4" />,
            label: 'Categorise uncategorised',
            onClick: () => handleCategorize('uncategorised'),
        },
        {
            key: 'all',
            icon: <RefreshCw className="h-4 w-4" />,
            label: 'Force recategorise all',
            onClick: () => handleCategorize('all'),
        },
        { type: 'divider' as const, key: 'fsg-divider' },
        {
            key: 'identify-fsg',
            icon: <FileSearch className="h-4 w-4" />,
            label: 'Identify FSG obligations',
            onClick: () => handleIdentifyFsg('unidentified'),
        },
        {
            key: 'force-identify-fsg',
            icon: <RefreshCw className="h-4 w-4" />,
            label: 'Force re-identify all FSG',
            onClick: () => handleIdentifyFsg('all'),
        },
    ];

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="content" />

            <SendObligationToOrganizationModal
                open={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                obligationIds={tableProps.rowSelection?.selectedRowKeys?.map((key) => BigInt(String(key))) || []}
                title="Assign obligations to register"
                onSuccess={() => {
                    tableProps.clearSelection();
                    void refetch();
                }}
            />

            <PageLayout.Header
                primaryActionAddon={
                    <Space>
                        {!isReadOnly && (
                            <>
                                <Tooltip title="Generate vector embeddings for semantic search and obligation matching">
                                    <Dropdown
                                        menu={{ items: embeddingMenuItems }}
                                        trigger={['click']}
                                        disabled={generateEmbeddingsLoading}
                                    >
                                        <Button type="secondary" icon={<Brain />} loading={generateEmbeddingsLoading}>
                                            Generate embeddings
                                        </Button>
                                    </Dropdown>
                                </Tooltip>
                                <Tooltip title="Use AI to automatically categorise obligations by type and regulation">
                                    <Dropdown
                                        menu={{ items: categoriseMenuItems }}
                                        trigger={['click']}
                                        disabled={categorizeLoading || identifyFsgLoading}
                                    >
                                        <Button
                                            type="secondary"
                                            icon={<Tags />}
                                            loading={categorizeLoading || identifyFsgLoading}
                                        >
                                            Categorise
                                        </Button>
                                    </Dropdown>
                                </Tooltip>
                                <Button
                                    type="secondary"
                                    icon={<Download />}
                                    onClick={() => setIsImportModalVisible(true)}
                                >
                                    Import CSV
                                </Button>
                            </>
                        )}
                        <Button type="secondary" icon={<Upload />} onClick={handleExport} loading={isExporting}>
                            Export CSV
                        </Button>
                        {!isReadOnly && (
                            <Button
                                type="secondary"
                                onClick={() => setIsModalVisible(true)}
                                icon={<Send />}
                                disabled={!tableProps.rowSelection?.selectedRowKeys?.length}
                            >
                                Send to organisation
                            </Button>
                        )}
                    </Space>
                }
            />

            <div className="mb-4">
                <MasterObligationFilters onChange={(where) => setQueryParams((prev) => ({ ...prev, where }))} />
            </div>

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/admin-console/platform-content/obligations/library/details/$id/manage',
                                params: {
                                    id,
                                },
                            },
                            e,
                        );
                    },
                })}
                scroll={{ x: 'max-content' }}
            />

            <ImportMasterObligationModal
                open={isImportModalVisible}
                onClose={() => setIsImportModalVisible(false)}
                onSuccess={() => {
                    setIsImportModalVisible(false);
                    void refetch();
                }}
            />

            <Modal
                title="Confirm Force Update All Embeddings"
                open={isConfirmForceUpdateVisible}
                onCancel={() => setIsConfirmForceUpdateVisible(false)}
                onOk={() => {
                    setIsConfirmForceUpdateVisible(false);
                    message.loading('Force updating all embeddings...');
                    generateEmbeddings(
                        { mode: 'all' },
                        {
                            onSuccess(data) {
                                message.destroy();
                                const jobId = data?.jobId;
                                if (jobId) {
                                    showJobSuccessModal({
                                        jobId,
                                        title: 'Force embeddings update queued',
                                        message: 'The force embeddings update has been queued for processing.',
                                        description:
                                            'This will regenerate embeddings for ALL obligation library items and may take considerable time depending on the number of obligations.',
                                    });
                                }
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        },
                    );
                }}
                okText="Confirm"
                cancelText="Cancel"
                confirmLoading={generateEmbeddingsLoading}
            >
                <Space orientation="vertical" className="w-full">
                    <p>
                        <strong>Warning:</strong> This will regenerate embeddings for ALL obligation library items,
                        regardless of whether they have been updated.
                    </p>
                    <p>This process may take considerable time and will consume API tokens.</p>
                    <p>Are you sure you want to proceed?</p>
                </Space>
            </Modal>

            <Modal
                title="Confirm force recategorise all"
                open={isConfirmForceCategoriseVisible}
                onCancel={() => setIsConfirmForceCategoriseVisible(false)}
                onOk={() => {
                    setIsConfirmForceCategoriseVisible(false);
                    message.loading('Force recategorising all obligations...');
                    categorizeObligations(
                        { mode: 'all' },
                        {
                            onSuccess(data) {
                                message.destroy();
                                const jobId = data?.jobId;
                                if (jobId) {
                                    showJobSuccessModal({
                                        jobId,
                                        title: 'Force recategorisation queued',
                                        message: 'The force recategorisation has been queued for processing.',
                                        description:
                                            'This will recategorise ALL obligation library items and may take considerable time depending on the number of obligations.',
                                    });
                                }
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        },
                    );
                }}
                okText="Confirm"
                cancelText="Cancel"
                confirmLoading={categorizeLoading}
            >
                <Space orientation="vertical" className="w-full">
                    <p>
                        <strong>Warning:</strong> This will recategorise ALL obligation library items, regardless of
                        whether they already have categories.
                    </p>
                    <p>This process may take considerable time and will consume API tokens.</p>
                    <p>Are you sure you want to proceed?</p>
                </Space>
            </Modal>

            <Modal
                title="Confirm force re-identify all FSG"
                open={isConfirmForceIdentifyFsgVisible}
                onCancel={() => setIsConfirmForceIdentifyFsgVisible(false)}
                onOk={() => {
                    setIsConfirmForceIdentifyFsgVisible(false);
                    message.loading('Force re-identifying all FSG obligations...');
                    identifyFsgObligations(
                        { mode: 'all' },
                        {
                            onSuccess(data) {
                                message.destroy();
                                const jobId = data?.jobId;
                                if (jobId) {
                                    showJobSuccessModal({
                                        jobId,
                                        title: 'Force FSG re-identification queued',
                                        message: 'The force FSG re-identification has been queued for processing.',
                                        description:
                                            'This will re-check ALL obligation library items for FSG relevance and may take considerable time depending on the number of obligations.',
                                    });
                                }
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        },
                    );
                }}
                okText="Confirm"
                cancelText="Cancel"
                confirmLoading={identifyFsgLoading}
            >
                <Space orientation="vertical" className="w-full">
                    <p>
                        <strong>Warning:</strong> This will re-check ALL obligation library items for FSG relevance,
                        regardless of whether they have already been identified.
                    </p>
                    <p>
                        Obligations previously marked as FSG-related may be unmarked if the AI determines they are not
                        FSG-related.
                    </p>
                    <p>This process may take considerable time and will consume API tokens.</p>
                    <p>Are you sure you want to proceed?</p>
                </Space>
            </Modal>
        </PageController>
    );
}
